import PermissionButton from "../../components/ui/PermissionButton";
import { useEffect, useMemo, useState } from "react";
import {
  X,
  Calendar,
  FileText,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
} from "lucide-react";

export default function AccountModal({ account, onClose }) {
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    if (!account) return;

    fetch(`/api/ledger/${account.id}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "success") {
          setLedger(data);
        }
      });
  }, [account]);

  if (!ledger) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 text-white">
        Loading...
      </div>
    );
  }

  const transactions = ledger.transactions || [];

  const totalDebit = transactions.reduce(
    (sum, t) => sum + Number(t.debit || 0),
    0,
  );

  const totalCredit = transactions.reduce(
    (sum, t) => sum + Number(t.credit || 0),
    0,
  );

  const closing = ledger.opening + totalDebit - totalCredit;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="w-full max-w-6xl rounded-3xl bg-[#09090B] border border-white/10 overflow-hidden">
        {/* Header */}

        <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
          <div>
            <h2 className="text-3xl font-serif">{account.name}</h2>

            <p className="text-sm text-gray-400 mt-1">Ledger Details</p>
          </div>

          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="w-10 h-10 rounded-xl bg-white/5 hover:bg-red-500/20 flex items-center justify-center"
          >
            <X size={20} />
          </PermissionButton>
        </div>

        {/* KPI */}

        <div className="grid md:grid-cols-4 gap-6 p-8">
          <SummaryCard
            title="Current Balance"
            value={closing}
            color="text-amber-400"
            icon={<Wallet size={18} />}
          />

          <SummaryCard
            title="Debit"
            value={totalDebit}
            color="text-emerald-400"
            icon={<ArrowDownLeft size={18} />}
          />

          <SummaryCard
            title="Credit"
            value={totalCredit}
            color="text-rose-400"
            icon={<ArrowUpRight size={18} />}
          />

          <SummaryCard
            title="Transactions"
            value={transactions.length}
            color="text-indigo-400"
            icon={<FileText size={18} />}
            currency={false}
          />
        </div>

        {/* Transactions */}

        <div className="px-8 pb-8">
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr className="text-gray-400">
                  <th className="text-left px-6 py-4">Date</th>

                  <th className="text-left">Voucher</th>

                  <th className="text-left">Description</th>

                  <th className="text-right">Debit</th>

                  <th className="text-right">Credit</th>

                  <th className="text-right pr-6">Balance</th>
                </tr>
              </thead>

              <tbody>
                {transactions.map((t, index) => (
                  <tr
                    key={index}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="px-6 py-4 flex items-center gap-2">
                      <Calendar size={15} />

                      {t.date}
                    </td>

                    <td>{t.voucher}</td>

                    <td>{t.description || "-"}</td>

                    <td className="text-right text-emerald-400">
                      {Number(t.debit) > 0
                        ? `₹${Number(t.debit).toLocaleString("en-IN")}`
                        : "-"}
                    </td>

                    <td className="text-right text-rose-400">
                      {Number(t.credit) > 0
                        ? `₹${Number(t.credit).toLocaleString("en-IN")}`
                        : "-"}
                    </td>

                    <td className="text-right pr-6 font-medium text-amber-400">
                      ₹{Number(t.balance).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}

        <div className="border-t border-white/10 bg-white/5">
          <div className="grid md:grid-cols-3 gap-6 p-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">
                Opening Balance
              </p>

              <h3 className="mt-2 text-2xl font-serif">
                ₹{Number(ledger.opening).toLocaleString("en-IN")}
              </h3>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">
                Closing Balance
              </p>

              <h3 className="mt-2 text-2xl font-serif text-amber-400">
                ₹{Number(closing).toLocaleString("en-IN")}
              </h3>
            </div>

            <div className="flex justify-end items-end">
              <PermissionButton permissionModule="accounts"
                onClick={onClose}
                className="px-8 py-3 rounded-xl bg-amber-400 text-black font-semibold"
              >
                Close
              </PermissionButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, color, icon, currency = true }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <div className="flex justify-between items-center">
        <p className="text-xs uppercase tracking-widest text-gray-400">
          {title}
        </p>

        {icon}
      </div>

      <h2 className={`mt-4 text-3xl font-serif ${color}`}>
        {currency ? `₹${Number(value).toLocaleString("en-IN")}` : value}
      </h2>
    </div>
  );
}
